import {
    Callout,
    DefaultButton,
    DialogContent,
    ICalloutPositionedInfo,
    PrimaryButton,
    Stack,
    VerticalDivider,
} from "@fluentui/react";
import React, {FC, useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useForceUpdate, useId,} from "@fluentui/react-hooks";
import {hr} from "../locales/hr";

export interface IAreYouSure {
    title: keyof typeof hr;
    onYes?: (data?: any) => void;
    onNo?: (data?: any) => void;
}

interface IOnixCallout {
    onDismiss?: () => void;
    title?: keyof typeof hr;
    trigger: (
        id: string,
        show: (
            e: React.MouseEvent<any, any> | undefined,
            data: any,
            forceStatus?: boolean,
            renderData?: any,
        ) => void,
    ) => JSX.Element;
    preventDismissOnScroll?: boolean;
    children?: (
        realign: () => void,
        show: () => void,
        renderData?: any,
    ) => JSX.Element | null;
    paddingLess?: boolean;
    areYouSure?: IAreYouSure;
    heightless?: boolean;
    widthless?: boolean;
    minWidth?: any;
    keyData?: string;
    customMaxWidth?: any;
    preventFocus?: boolean;
    titleless?: boolean;
    disabled?: boolean;
}

export const OnixCallout: FC<IOnixCallout> = ({
                                                  trigger,
                                                  titleless,
                                                  disabled,
                                                  paddingLess,
                                                  preventFocus,
                                                  preventDismissOnScroll,
                                                  onDismiss,
                                                  title,
                                                  children,
                                                  areYouSure,
                                                  heightless,
                                                  widthless,
                                                  minWidth,
                                                  keyData,
                                                  customMaxWidth,
                                              }) => {
    const {t} = useTranslation();
    const [show, setShow] = useState(false);
    const forceUpdate = useForceUpdate();
    const [temporaryData, setTemporaryData] = useState<any>();
    const [renderData, setRenderData] = useState<any>();
    const [closing, setClosing] = useState(false);

    const id = useId("onix-callout");

    const realign = useCallback(() => {
        if (forceUpdate) {
            forceUpdate();
        }
    }, [forceUpdate]);

    const toggleShow = useCallback(
        (
            e: React.MouseEvent<any, any> | undefined,
            temporaryData: any,
            forceStatus?: boolean,
            renderData?: any,
        ) => {
            if (e) {
                if (e?.preventDefault) e?.preventDefault();
                if (e?.stopPropagation) e?.stopPropagation();
                // e.stopImmediatePropagation();
            }
            if (forceStatus !== undefined) {
                setShow(forceStatus);
            } else {
                setShow(!show);
            }
            setTemporaryData(temporaryData);
            setRenderData(renderData);
        },
        [show],
    );

    const styles = useMemo(
        () => ({
            topButton: {color: "red", padding: paddingLess ? 5 : undefined},
            inner: {padding: paddingLess ? 0 : undefined},
            title: {padding: paddingLess ? 10 : undefined},
        }),
        [paddingLess],
    );

    const dismissDialogClicked = useCallback(() => {
        setClosing(true);
        if (onDismiss) onDismiss();
        toggleShow(undefined, undefined, false);
    }, [toggleShow, onDismiss]);

    const onYesClicked = useCallback(() => {
        if (areYouSure?.onYes) areYouSure.onYes(temporaryData);
        setShow(false);
        if (onDismiss) onDismiss();
    }, [areYouSure?.onYes, temporaryData, setShow, onDismiss]);

    const onNoClicked = useCallback(() => {
        if (areYouSure?.onNo) areYouSure.onNo(temporaryData);
        setShow(false);
        if (onDismiss) onDismiss();
    }, [areYouSure?.onNo, temporaryData, setShow, onDismiss]);

    const onDismissCalloutOuter = useCallback(() => {
        setClosing(true);
        if (onDismiss) onDismiss();
        toggleShow(undefined, undefined, false);
    }, [toggleShow, onDismiss]);

    const onCalloutPositioned = useCallback(
        (positions?: ICalloutPositionedInfo | undefined) => ({
            ...positions,
            elementPosition: positions?.elementPosition.right,
        }),
        [],
    );

    const childrenShowClicked = useCallback(
        () => toggleShow(undefined, undefined),
        [toggleShow],
    );

    const toggleShowFromTrigger = useCallback(
        (
            e: React.MouseEvent<any, any> | undefined,
            temporaryData: any,
            forceStatus?: boolean,
            renderData?: any,
        ) => {
            toggleShow(e, temporaryData, forceStatus, renderData);
        },
        [toggleShow, closing],
    );

    return (
        <>
            {trigger(id, toggleShowFromTrigger)}
            <div key={keyData} style={{position: "absolute"}}>
                {!!show ? (
                    <Callout
                        onPositioned={onCalloutPositioned}
                        calloutMaxHeight={heightless ? undefined : 350}
                        calloutMaxWidth={
                            widthless ? undefined : areYouSure ? 300 : customMaxWidth
                        }
                        calloutMinWidth={minWidth}
                        preventDismissOnLostFocus={false}
                        role="alertdialog"
                        gapSpace={0}
                        preventDismissOnScroll={preventDismissOnScroll != false}
                        target={`#${id}`}
                        onDismiss={onDismissCalloutOuter}
                        dismissOnTargetClick={true}
                        setInitialFocus={!preventFocus}
                    >
                        <DialogContent
                            title={!titleless ? t(title ?? "warning") : undefined}
                            showCloseButton
                            styles={styles}
                            onDismiss={dismissDialogClicked}
                            titleProps={{
                                style: titleless ? {display: "none"} : undefined,
                            }}
                        >
                            {!!children ? (
                                children(realign, childrenShowClicked, renderData)
                            ) : (
                                <span>
                  {areYouSure
                      ? t("are_you_sure_you_want_to") +
                      " " +
                      t(areYouSure.title) +
                      (t(areYouSure.title).endsWith("?") ? "" : "?")
                      : null}
                </span>
                            )}
                        </DialogContent>
                        {!!areYouSure ? (
                            <Stack
                                horizontal
                                horizontalAlign="end"
                                tokens={{padding: "0 15px 15px 15px"}}
                            >
                                <PrimaryButton text={t("yes")} onClick={onYesClicked}/>
                                <VerticalDivider/>
                                <DefaultButton text={t("no")} onClick={onNoClicked}/>
                            </Stack>
                        ) : null}
                    </Callout>
                ) : null}
            </div>
        </>
    );
};
